import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function BankDetailsPage() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch: ''
  });

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const fetchBankDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/bank-details`, {
        headers: getAuthHeaders()
      });
      if (response.data && response.data.account_holder_name) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch bank details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API_URL}/bank-details`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('Bank details saved successfully');
    } catch (error) {
      toast.error('Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sidebar>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bank Details
          </h1>
          <p className="text-slate-600 mt-2">Manage your payment details</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading bank details...</div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 space-y-6">
            <div>
              <Label htmlFor="account_holder_name">Account Holder Name *</Label>
              <Input
                id="account_holder_name"
                value={formData.account_holder_name}
                onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
                required
                data-testid="account-holder-name-input"
              />
            </div>

            <div>
              <Label htmlFor="bank_name">Bank Name *</Label>
              <Input
                id="bank_name"
                value={formData.bank_name}
                onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                required
                data-testid="bank-name-input"
              />
            </div>

            <div>
              <Label htmlFor="account_number">Account Number *</Label>
              <Input
                id="account_number"
                value={formData.account_number}
                onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                required
                data-testid="account-number-input"
              />
            </div>

            <div>
              <Label htmlFor="ifsc_code">IFSC Code *</Label>
              <Input
                id="ifsc_code"
                value={formData.ifsc_code}
                onChange={(e) => setFormData({...formData, ifsc_code: e.target.value})}
                required
                data-testid="ifsc-code-input"
              />
            </div>

            <div>
              <Label htmlFor="branch">Branch *</Label>
              <Input
                id="branch"
                value={formData.branch}
                onChange={(e) => setFormData({...formData, branch: e.target.value})}
                required
                data-testid="branch-input"
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-bank-details-button"
                className="bg-[#0052CC] hover:bg-[#0043A8] text-white"
              >
                {saving ? 'Saving...' : 'Save Bank Details'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Sidebar>
  );
}